import { backendKeysForSlug, resolveBuyerSubSystem } from '../constants/buyerSubSystems';

export const CONDITION_OPTIONS = [
  { key: 'new_oem', label: 'New OEM' },
  { key: 'new_aftermarket', label: 'New Aftermarket' },
  { key: 'new_refurbished', label: 'New Refurbished' },
  { key: 'used_grade_a', label: 'Used Grade A' },
  { key: 'used_grade_b', label: 'Used Grade B' },
  { key: 'used_grade_c', label: 'Used Grade C' },
];

export const SELLER_TYPE_OPTIONS = [
  { key: 'oem_distributor', label: 'Verified OEM Distributor' },
  { key: 'scrapyard', label: 'Scrapyard' },
  { key: 'parts_shop', label: 'Parts Shop' },
  { key: 'individual', label: 'Individual Seller' },
];

export const PROXIMITY_OPTIONS = [
  { key: 'pan_india', label: 'PAN India', radiusKm: null },
  { key: '10', label: 'Within 10 km', radiusKm: 10 },
  { key: '30', label: 'Within 30 km', radiusKm: 30 },
  { key: '50', label: 'Within 50 km', radiusKm: 50 },
  { key: '100', label: 'Within 100 km', radiusKm: 100 },
];

export const RATING_OPTIONS = [
  { key: 'any', label: 'Any', minRating: null },
  { key: '4', label: '4★ and above', minRating: 4 },
  { key: '3', label: '3★ and above', minRating: 3 },
];

export const DELIVERY_OPTIONS = [
  { key: 'any', label: 'Any' },
  { key: 'same_day', label: 'Same day' },
  { key: 'next_day', label: 'Next day' },
  { key: '2_3_days', label: '2–3 days' },
];

export const DEFAULT_BROWSE_FILTERS = {
  conditions: [],
  minPrice: '',
  maxPrice: '',
  sellerTypes: [],
  proximity: 'pan_india',
  rating: 'any',
  delivery: 'any',
};

const SERVER_CONDITION_KEYS = new Set(
  CONDITION_OPTIONS.filter((c) => c.key !== 'new_refurbished').map((c) => c.key)
);

export function parseBrowseFiltersFromSearchParams(searchParams) {
  const conditions = (searchParams.get('conditions') || '')
    .split(',')
    .map((v) => v.trim())
    .filter(Boolean);

  const sellerTypes = (searchParams.get('sellerTypes') || '')
    .split(',')
    .map((v) => v.trim())
    .filter(Boolean);

  return {
    conditions,
    minPrice: searchParams.get('minPrice') || '',
    maxPrice: searchParams.get('maxPrice') || '',
    sellerTypes,
    proximity: searchParams.get('proximity') || DEFAULT_BROWSE_FILTERS.proximity,
    rating: searchParams.get('rating') || DEFAULT_BROWSE_FILTERS.rating,
    delivery: searchParams.get('delivery') || DEFAULT_BROWSE_FILTERS.delivery,
  };
}

export function writeBrowseFiltersToSearchParams(filters, existing = new URLSearchParams()) {
  const params = new URLSearchParams(existing);

  if (filters.conditions?.length) params.set('conditions', filters.conditions.join(','));
  else params.delete('conditions');

  if (filters.minPrice) params.set('minPrice', String(filters.minPrice));
  else params.delete('minPrice');

  if (filters.maxPrice) params.set('maxPrice', String(filters.maxPrice));
  else params.delete('maxPrice');

  if (filters.sellerTypes?.length) params.set('sellerTypes', filters.sellerTypes.join(','));
  else params.delete('sellerTypes');

  if (filters.proximity && filters.proximity !== 'pan_india') params.set('proximity', filters.proximity);
  else params.delete('proximity');

  if (filters.rating && filters.rating !== 'any') params.set('rating', filters.rating);
  else params.delete('rating');

  if (filters.delivery && filters.delivery !== 'any') params.set('delivery', filters.delivery);
  else params.delete('delivery');

  return params;
}

export function countActiveBrowseFilters(filters) {
  let n = 0;
  if (filters.conditions?.length) n += 1;
  if (filters.minPrice || filters.maxPrice) n += 1;
  if (filters.sellerTypes?.length) n += 1;
  if (filters.proximity && filters.proximity !== 'pan_india') n += 1;
  if (filters.rating && filters.rating !== 'any') n += 1;
  if (filters.delivery && filters.delivery !== 'any') n += 1;
  return n;
}

export function buildServerBrowseQuery({ subSystemSlug, filters, coords, vehicleParams = {} }) {
  const entry = resolveBuyerSubSystem(subSystemSlug);
  const backendKeys = entry ? backendKeysForSlug(entry.slug) : [];

  const serverConditions = (filters.conditions || []).filter((c) => SERVER_CONDITION_KEYS.has(c));
  const ratingOpt = RATING_OPTIONS.find((r) => r.key === filters.rating);
  const proximityOpt = PROXIMITY_OPTIONS.find((p) => p.key === filters.proximity);

  const query = {
    limit: 120,
    page: 1,
    ...vehicleParams,
  };

  if (backendKeys.length) query.subSystem = backendKeys.join(',');

  if (serverConditions.length) query.listingCondition = serverConditions.join(',');

  if (filters.sellerTypes?.length) query.sellerTypes = filters.sellerTypes.join(',');

  if (ratingOpt?.minRating) query.minRating = ratingOpt.minRating;

  if (proximityOpt?.radiusKm && coords?.lat != null && coords?.lng != null) {
    query.proximity = '1';
    query.latitude = coords.lat;
    query.longitude = coords.lng;
    query.radiusKm = proximityOpt.radiusKm;
  }

  return query;
}

function productEffectiveRating(product) {
  const vendor = product.vendorId;
  return Number(
    product.rating ||
      product.sellerRating ||
      vendor?.vendorDetails?.rating ||
      vendor?.rating ||
      0
  );
}

function productListingCondition(product) {
  if (product.listingCondition) return product.listingCondition;
  const pt = String(product.partType || '').toUpperCase();
  if (pt === 'USED') {
    const g = String(product.usedGrade || '').toUpperCase();
    if (g === 'A') return 'used_grade_a';
    if (g === 'B') return 'used_grade_b';
    if (g === 'C') return 'used_grade_c';
    return 'used_grade_b';
  }
  if (product.origin === 'OEM' || pt === 'NEW') return 'new_oem';
  return 'new_aftermarket';
}

function isRefurbishedProduct(product) {
  const hay = `${product.name || ''} ${product.description || ''}`.toLowerCase();
  return hay.includes('refurb') || hay.includes('recon') || product.isRefurbished === true;
}

export function estimateDeliveryDays(product, buyerCoords) {
  const dispatchHours = Number(product.dispatchWindowHours ?? product.expressDeliveryMeta?.dispatchHours ?? 48);
  const dispatchDays = dispatchHours / 24;
  const baseTransit = Number(product.estimatedTransitDays ?? product.expressDeliveryMeta?.transitDays);
  let transitDays = Number.isFinite(baseTransit) ? baseTransit : 3;

  const vendor = product.vendorId;
  const vLat = parseFloat(vendor?.address?.latitude ?? vendor?.vendorDetails?.latitude);
  const vLng = parseFloat(vendor?.address?.longitude ?? vendor?.vendorDetails?.longitude);
  if (buyerCoords?.lat != null && buyerCoords?.lng != null && Number.isFinite(vLat) && Number.isFinite(vLng)) {
    const km = haversineKm(buyerCoords.lat, buyerCoords.lng, vLat, vLng);
    transitDays = Math.max(0, Math.ceil(km / 250));
  } else {
    const parsed = parseInt(String(product.deliveryTime || '').replace(/[^\d]/g, ''), 10);
    if (Number.isFinite(parsed) && parsed > 0) transitDays = parsed;
  }

  return dispatchDays + transitDays;
}

function haversineKm(lat1, lng1, lat2, lng2) {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export function matchesDeliveryFilter(product, deliveryKey, buyerCoords) {
  if (!deliveryKey || deliveryKey === 'any') return true;
  const totalDays = estimateDeliveryDays(product, buyerCoords);
  if (deliveryKey === 'same_day') return totalDays <= 1;
  if (deliveryKey === 'next_day') return totalDays <= 2;
  if (deliveryKey === '2_3_days') return totalDays <= 4;
  return true;
}

export function applyClientBrowseFilters(products, filters, buyerCoords) {
  let list = Array.isArray(products) ? [...products] : [];

  const wantsRefurbished = filters.conditions?.includes('new_refurbished');
  const clientConditions = (filters.conditions || []).filter((c) => c !== 'new_refurbished');

  if (clientConditions.length || wantsRefurbished) {
    list = list.filter((p) => {
      const cond = productListingCondition(p);
      const condMatch = !clientConditions.length || clientConditions.includes(cond);
      const refurbMatch = !wantsRefurbished || isRefurbishedProduct(p);
      if (wantsRefurbished && clientConditions.length) return condMatch || refurbMatch;
      if (wantsRefurbished) return refurbMatch;
      return condMatch;
    });
  }

  const minP = parseFloat(filters.minPrice);
  const maxP = parseFloat(filters.maxPrice);
  if (Number.isFinite(minP)) list = list.filter((p) => Number(p.price) >= minP);
  if (Number.isFinite(maxP)) list = list.filter((p) => Number(p.price) <= maxP);

  if (filters.delivery && filters.delivery !== 'any') {
    list = list.filter((p) => matchesDeliveryFilter(p, filters.delivery, buyerCoords));
  }

  return list;
}

export function computePriceRangeHints(products) {
  const prices = (products || []).map((p) => Number(p.price)).filter((n) => Number.isFinite(n) && n > 0);
  if (!prices.length) return { floor: 0, ceiling: 50000 };
  return {
    floor: Math.floor(Math.min(...prices)),
    ceiling: Math.ceil(Math.max(...prices)),
  };
}

export function effectiveProductRating(product) {
  return productEffectiveRating(product);
}
